import { type FormEvent, useEffect, useState } from "react";
import { Button } from "@/components/ui/button.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialIp?: string;
  onSubmit: (body: { ip: string; duration: string; reason: string }) => Promise<void>;
};

export function BanIpDialog({ open, onOpenChange, initialIp = "", onSubmit }: Props) {
  const [ip, setIp] = useState(initialIp);
  const [duration, setDuration] = useState("4h");
  const [reason, setReason] = useState("firewall-console-ban");
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (open) {
      setIp(initialIp);
      setDuration("4h");
      setReason("firewall-console-ban");
    }
  }, [open, initialIp]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setPending(true);
    try {
      await onSubmit({ ip, duration, reason });
      onOpenChange(false);
      setIp("");
    } catch {
      /* keep dialog open; toast from mutation */
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ban-ip-dialog">
        <DialogHeader>
          <DialogTitle>Ban IP</DialogTitle>
          <DialogDescription>Add a temporary ban decision via the API. This affects CrowdSec decisions only.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ban-ip">IP</Label>
            <Input id="ban-ip" value={ip} onChange={(ev) => setIp(ev.target.value)} required placeholder="203.0.113.1" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ban-duration">Duration</Label>
            <Input id="ban-duration" value={duration} onChange={(ev) => setDuration(ev.target.value)} aria-label="duration" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ban-reason">Reason</Label>
            <Input id="ban-reason" value={reason} onChange={(ev) => setReason(ev.target.value)} aria-label="reason" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={pending}>
              {pending ? "Applying…" : "Ban IP"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
